import { useState } from 'react';
import { Row, Col, Card, Badge, Form, Button, Spinner, Modal, Table } from 'react-bootstrap';
import { HiOutlineExclamationCircle, HiOutlineChip, HiOutlineClipboardList, HiOutlineDownload } from 'react-icons/hi';
import { FaBuilding } from 'react-icons/fa';
import Api from '../../../services/Api';
import { NotifyModal } from '../../../components/NotifyModal/NotifyModal';

const verde = "#1c4220";
const verdeClaro = "#8fad15";

const getSemtEquipments = (unit) =>
  (Array.isArray(unit?.equipments) ? unit.equipments : [])
    .filter(s => String(s).toUpperCase().includes('SEMT'));

const SemtSelector = ({ equipId, semtEquipments, linked, onToggle }) => {
  const available = semtEquipments
    .filter(s => !linked.includes(s))
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

  return (
    <div className="mt-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <Form.Label className="small fw-bold mb-0" style={{ color: verde }}>
          Equipamentos Vinculados
        </Form.Label>
        <small className="text-muted">
          {linked.length} de {semtEquipments.length} selecionado(s)
        </small>
      </div>

      {/* Select para adicionar */}
      <Form.Select
        size="sm"
        value=""
        onChange={e => { if (e.target.value) onToggle(equipId, e.target.value, linked); }}
        style={{ borderColor: `${verde}60` }}
        disabled={available.length === 0}
      >
        <option value="">
          {available.length === 0 ? 'Todos os equipamentos vinculados' : 'Selecione para adicionar...'}
        </option>
        {available.map(serial => (
          <option key={serial} value={serial}>{serial}</option>
        ))}
      </Form.Select>

      {/* Badges dos vinculados */}
      {linked.length > 0 && (
        <div className="d-flex flex-wrap gap-1 mt-2">
          {linked.map(serial => (
            <Badge
              key={serial}
              bg="success"
              className="d-flex align-items-center"
              style={{ fontSize: '0.75rem' }}
            >
              {serial}
              <span
                onClick={() => onToggle(equipId, serial, linked)}
                title="Remover"
                style={{ cursor: 'pointer', fontWeight: 'bold', marginLeft: '6px', lineHeight: 1 }}
              >
                ×
              </span>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};

const AlarmConfigForm = ({ user, unit, alarmEquipments, onAlarmUpdate }) => {
  const [editState, setEditState] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [successId, setSuccessId] = useState(null);
  const [testingId, setTestingId] = useState(null);
  const [modalShow, setModalShow] = useState(false);
  const [modalType, setModalType] = useState({ color: '', status: '' });
  const [logsModal, setLogsModal] = useState({ show: false, equip: null, logs: [], loading: false });

  const semtEquipments = getSemtEquipments(unit);

  const showError = (msg) => {
    setModalType({ color: 'red', status: msg });
    setModalShow(true);
  };

  const showSuccess = (msg) => {
    setModalType({ color: '#198754', status: msg });
    setModalShow(true);
  };

  const getField = (equip, field) =>
    editState[equip._id]?.[field] !== undefined
      ? editState[equip._id][field]
      : equip[field];

  const handleChange = (equipId, field, value) => {
    setEditState(prev => ({
      ...prev,
      [equipId]: { ...prev[equipId], [field]: value }
    }));
  };

  const toggleSemt = (equipId, serial, currentLinked) => {
    const linked = Array.isArray(currentLinked) ? currentLinked : [];
    const updated = linked.includes(serial)
      ? linked.filter(s => s !== serial)
      : [...linked, serial];
    setEditState(prev => ({
      ...prev,
      [equipId]: { ...prev[equipId], equipments: updated }
    }));
  };

  const handleSave = async (equip) => {
    setSavingId(equip._id);
    try {
      const changes = editState[equip._id] || {};

      const minHumidity = changes.minHumidity ?? equip.minHumidity ?? '';
      const maxHumidity = changes.maxHumidity ?? equip.maxHumidity ?? '';

      if (minHumidity !== '' && maxHumidity !== '' && Number(minHumidity) > Number(maxHumidity)) {
        showError('A umidade mínima não pode ser maior que a umidade máxima!');
        setSavingId(null);
        return;
      }

      const payload = {
        active: changes.active ?? equip.active ?? false,
        alarm: changes.alarm ?? equip.alarm,
        silenciar: Number(changes.silenciar ?? equip.silenciar),
        rearm_rst: Number(changes.rearm_rst ?? equip.rearm_rst),
        rearm_buzzer: Number(changes.rearm_buzzer ?? equip.rearm_buzzer),
        description: changes.description ?? equip.description ?? '',
        minHumidity,
        maxHumidity,
        equipments: changes.equipments ?? equip.equipments ?? [],
        lastEditedByEmail: user?.email ?? '',
      };

      const { data: updatedAlarm } = await Api.put(`/alarm/v1/${encodeURIComponent(equip.serialNumber)}`, payload);

      onAlarmUpdate?.(updatedAlarm);
      setEditState(prev => ({ ...prev, [equip._id]: undefined }));
      setSuccessId(equip._id);
      setTimeout(() => setSuccessId(null), 2000);
    } catch (e) {
      showError('Erro ao salvar configuração do alarme!');
      console.error(e);
    }
    setSavingId(null);
  };

  const handleTest = async (equip, currentAlarmOn) => {
    const newState = currentAlarmOn ? 'OFF' : 'ON';
    setTestingId(equip._id);
    try {
      const { data: updatedAlarm } = await Api.put(
        `/alarm/v1/${encodeURIComponent(equip.serialNumber)}`,
        { alarm: newState }
      );
      onAlarmUpdate?.(updatedAlarm);
      handleChange(equip._id, 'alarm', newState);
    } catch (e) {
      showError(`Erro ao ${newState === 'ON' ? 'ativar' : 'desativar'} o alarme para teste!`);
      console.error(e);
    }
    setTestingId(null);
  };

  const handleDownloadCsv = () => {
    const { logs, equip } = logsModal;
    if (!logs.length) return;

    const headers = ['Data', 'Equipamento', 'Nome', 'Status', 'Umidade (%)', 'Temperatura (°C)', 'PH', 'Grão', 'Umid. Min', 'Umid. Máx', 'Configuração feita por'];
    const rows = logs.map(log => [
      log.date ? new Date(log.date).toLocaleString('pt-BR') : '',
      log.serialnumber || '',
      log.nameEquipament || log.localName || '',
      log.alarmStatus || '',
      log.humidity ?? '',
      log.temperature ?? '',
      log.ph ?? '',
      log.grain || '',
      log.minHumidity || '',
      log.maxHumidity || '',
      log.lastEditedByEmail || '',
    ]);

    const csv = [headers, ...rows]
      .map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(';'))
      .join('\n');

    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `logs_${equip?.serialNumber}_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleViewLogs = async (equip) => {
    setLogsModal({ show: true, equip, logs: [], loading: true });
    try {
      const { data } = await Api.get('/alarm/v1/activation-history', {
        params: { serialnumber: equip.serialNumber, companyId: equip.companyId }
      });
      setLogsModal(prev => ({ ...prev, logs: data, loading: false }));
    } catch (e) {
      console.error(e);
      setLogsModal(prev => ({ ...prev, loading: false }));
    }
  };

  const alarmStatus = (value) => {
    const v = String(value).toUpperCase();
    return v === 'ON' || v === 'ATIVO' || v === 'TRUE' || v === '1';
  };

  return (
    <div>
      <NotifyModal type={modalType} show={modalShow} onHide={() => setModalShow(false)} />

      {/* Cabeçalho da unidade */}
      <Card className="border-0 shadow-sm mb-2">
        <Card.Header>
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-2">
              <FaBuilding style={{ color: 'white' }} size={20} />
              <h5 className="mb-0 fw-semibold text-white">
                {user?.hideSensitiveInfo ? '*****' : unit?.name?.toUpperCase() || 'Empresa sem nome'}
              </h5>
            </div>
            <Badge pill bg="success" className="px-3 py-2" style={{ fontSize: '0.85rem' }}>
              {alarmEquipments.length} {alarmEquipments.length === 1 ? 'alarme' : 'alarmes'}
            </Badge>
          </div>
        </Card.Header>
      </Card>

      {/* Cards dos equipamentos ALRM */}
      {alarmEquipments.length === 0 ? (
        <div className="d-flex flex-column align-items-center justify-content-center py-5 text-center">
          <HiOutlineExclamationCircle size={52} style={{ color: '#6c757d', opacity: 0.4 }} />
          <p className="mt-3 text-muted">Nenhum equipamento ALRM encontrado nesta unidade</p>
        </div>
      ) : (
        <Row className="g-2">
          {alarmEquipments.map((equip) => {
            const isSaving = savingId === equip._id;
            const isSuccess = successId === equip._id;
            const alarmOn = alarmStatus(getField(equip, 'alarm'));
            const isActive = getField(equip, 'active') === true ||
              getField(equip, 'active') === 'true';
            const minVal = getField(equip, 'minHumidity') ?? '';
            const maxVal = getField(equip, 'maxHumidity') ?? '';
            const humidityInvalid = minVal !== '' && maxVal !== '' && Number(minVal) > Number(maxVal);

            return (
              <Col xs={12} lg={6} key={equip._id}>
                <Card className="border-0 shadow-sm h-100">
                  <Card.Header className="d-flex justify-content-between align-items-center py-2 px-3">
                    <div className="d-flex align-items-center gap-2">
                      <HiOutlineChip size={20} style={{ color: '#fff' }} />
                      <span className="fw-bold" style={{ color: '#fff' }}>
                        {equip.serialNumber}
                      </span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <small className="text-muted">FW: {equip.fw || '--'}</small>
                      <Badge bg={alarmOn ? 'danger' : 'secondary'} style={{ fontSize: '0.75rem' }}>
                        {alarmOn ? 'ATIVO' : 'INATIVO'}
                      </Badge>
                      <Button
                        size="sm"
                        variant={alarmOn ? 'outline-secondary' : 'outline-danger'}
                        className="rounded-pill px-2 py-0"
                        style={{ fontSize: '0.75rem', borderWidth: '1.5px' }}
                        disabled={testingId === equip._id}
                        onClick={() => handleTest(equip, alarmOn)}
                        title={alarmOn ? 'Desativar alarme para teste' : 'Ativar alarme para teste'}
                      >
                        {testingId === equip._id
                          ? <><Spinner size="sm" className="me-1" />{alarmOn ? 'Desativando...' : 'Ativando...'}</>
                          : alarmOn ? 'Desativar' : 'Ativar'}
                      </Button>
                    </div>
                  </Card.Header>

                  <Card.Body className="px-3 py-3">
                    <Form>
                      {/* Status do alarme */}
                      <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold mb-1" style={{ color: verde }}>
                          Status do Alarme
                        </Form.Label>
                        <Form.Check
                          type="switch"
                          id={`active-switch-${equip._id}`}
                          label={isActive ? 'Habilitado' : 'Desabilitado'}
                          checked={isActive}
                          onChange={e => handleChange(equip._id, 'active', e.target.checked)}
                        />
                      </Form.Group>

                      {/* Descrição */}
                      <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold mb-1" style={{ color: verde }}>
                          Descrição
                        </Form.Label>
                        <Form.Control
                          size="sm"
                          type="text"
                          placeholder="Descrição do alarme..."
                          value={getField(equip, 'description') ?? ''}
                          onChange={e => handleChange(equip._id, 'description', e.target.value)}
                          style={{ borderColor: `${verde}60` }}
                        />
                      </Form.Group>

                      <Row className="g-3">
                        {/* Umidade Mínima */}
                        <Col xs={12} sm={6}>
                          <Form.Group>
                            <Form.Label className="small fw-bold mb-1" style={{ color: humidityInvalid ? '#dc3545' : verde }}>
                              Umidade Mínima (%)
                            </Form.Label>
                            <Form.Control
                              size="sm"
                              type="number"
                              min={0}
                              max={100}
                              value={minVal}
                              onChange={e => handleChange(equip._id, 'minHumidity', e.target.value)}
                              isInvalid={humidityInvalid}
                              style={{ borderColor: humidityInvalid ? '#dc3545' : `${verde}60` }}
                            />
                          </Form.Group>
                        </Col>

                        {/* Umidade Máxima */}
                        <Col xs={12} sm={6}>
                          <Form.Group>
                            <Form.Label className="small fw-bold mb-1" style={{ color: humidityInvalid ? '#dc3545' : verde }}>
                              Umidade Máxima (%)
                            </Form.Label>
                            <Form.Control
                              size="sm"
                              type="number"
                              min={0}
                              max={100}
                              value={maxVal}
                              onChange={e => handleChange(equip._id, 'maxHumidity', e.target.value)}
                              isInvalid={humidityInvalid}
                              style={{ borderColor: humidityInvalid ? '#dc3545' : `${verde}60` }}
                            />
                            {humidityInvalid && (
                              <Form.Control.Feedback type="invalid">
                                O mínimo não pode ser maior que o máximo.
                              </Form.Control.Feedback>
                            )}
                          </Form.Group>
                        </Col>

                        {/* Silenciar */}
                        <Col xs={12} sm={4}>
                          <Form.Group>
                            <Form.Label className="small fw-bold mb-1" style={{ color: verde }}>
                              Tempo de ativação da sirene
                            </Form.Label>
                            <Form.Control
                              size="sm"
                              type="number"
                              min={0}
                              value={getField(equip, 'silenciar') ?? ''}
                              onChange={e => handleChange(equip._id, 'silenciar', e.target.value)}
                              style={{ borderColor: `${verde}60` }}
                            />
                            <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                              Tempo que a sirene vai ficar ativada (em segundos)
                            </Form.Text>
                          </Form.Group>
                        </Col>

                        {/* Rearm Buzzer */}
                        <Col xs={12} sm={4}>
                          <Form.Group>
                            <Form.Label className="small fw-bold mb-1" style={{ color: verde }}>
                              Intervalo de rearme da sirene
                            </Form.Label>
                            <Form.Control
                              size="sm"
                              type="number"
                              min={0}
                              value={getField(equip, 'rearm_buzzer') ?? ''}
                              onChange={e => handleChange(equip._id, 'rearm_buzzer', e.target.value)}
                              style={{ borderColor: `${verde}60` }}
                            />
                            <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                              Tempo para rearme da sirene após parar de tocar (em segundos)
                            </Form.Text>
                          </Form.Group>
                        </Col>

                        {/* Rearm RST */}
                        <Col xs={12} sm={4}>
                          <Form.Group>
                            <Form.Label className="small fw-bold mb-1" style={{ color: verde }}>
                              Intervalo de rearme após reset
                            </Form.Label>
                            <Form.Control
                              size="sm"
                              type="number"
                              min={0}
                              value={getField(equip, 'rearm_rst') ?? ''}
                              onChange={e => handleChange(equip._id, 'rearm_rst', e.target.value)}
                              style={{ borderColor: `${verde}60` }}
                            />
                            <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                              Tempo para rearme após reset no painel de controle (em segundos)
                            </Form.Text>
                          </Form.Group>
                        </Col>

                      </Row>

                      {/* Equipamentos SEMT vinculados */}
                      {semtEquipments.length > 0 && (
                        <SemtSelector
                          equipId={equip._id}
                          semtEquipments={semtEquipments}
                          linked={Array.isArray(getField(equip, 'equipments') ?? equip.equipments)
                            ? (getField(equip, 'equipments') ?? equip.equipments)
                            : []}
                          onToggle={toggleSemt}
                        />
                      )}
                    </Form>
                  </Card.Body>

                  <Card.Footer className="bg-transparent border-0 px-3 pb-3 pt-1">
                    <div className="d-flex gap-2">
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        className="rounded-pill py-2 d-flex align-items-center gap-1"
                        style={{ fontWeight: '500', whiteSpace: 'nowrap' }}
                        onClick={() => handleViewLogs(equip)}
                      >
                        <HiOutlineClipboardList size={15} />
                        Ver Logs
                      </Button>
                      <Button
                        size="sm"
                        className="w-100 rounded-pill py-2"
                        disabled={isSaving}
                        onClick={() => handleSave(equip)}
                        style={{
                          backgroundColor: isSuccess ? verdeClaro : verde,
                          borderColor: isSuccess ? verdeClaro : verde,
                          color: isSuccess ? verde : 'white',
                          fontWeight: '600',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {isSaving ? (
                          <><Spinner size="sm" className="me-2" />Salvando...</>
                        ) : isSuccess ? (
                          <>✓ Salvo</>
                        ) : (
                          <>Salvar</>
                        )}
                      </Button>
                    </div>
                  </Card.Footer>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

      {/* Modal de logs */}
      <Modal
        show={logsModal.show}
        onHide={() => setLogsModal(prev => ({ ...prev, show: false }))}
        size="xl"
        centered
      >
        <Modal.Header closeButton style={{ borderBottom: `1px solid ${verde}20` }}>
          <Modal.Title style={{ color: verde, fontWeight: '600', fontSize: '1rem' }}>
            <HiOutlineClipboardList className="me-2" size={20} />
            Histórico de Ativações — {logsModal.equip?.serialNumber}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '75vh', overflowY: 'auto', padding: '0' }}>
          {logsModal.loading ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner animation="border" style={{ color: verde }} />
            </div>
          ) : logsModal.logs.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <HiOutlineClipboardList size={40} style={{ opacity: 0.3 }} />
              <p className="mt-2">Nenhum log encontrado</p>
            </div>
          ) : (
            <Table hover size="sm" className="mb-0">
              <thead style={{ position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}>
                <tr>
                  {['Data', 'Equipamento', 'Status', 'Umid. (%)', 'Temp. (°C)', 'PH', 'Grão', 'Umid. Min', 'Umid. Máx', 'Configuração feita por'].map(h => (
                    <th key={h} style={{ fontSize: '0.78rem', padding: '10px 12px', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logsModal.logs.map((log, i) => (
                  <tr key={i}>
                    <td style={{ fontSize: '0.78rem', padding: '8px 12px', whiteSpace: 'nowrap' }}>
                      {log.date ? new Date(log.date).toLocaleString('pt-BR') : '--'}
                    </td>
                    <td style={{ fontSize: '0.78rem', padding: '8px 12px' }}>
                      <div>{log.serialnumber || '--'}</div>
                      {(log.nameEquipament || log.localName) && (
                        <small className="text-muted">{log.nameEquipament || log.localName}</small>
                      )}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <Badge bg={log.alarmStatus === 'ON' ? 'danger' : 'secondary'} style={{ fontSize: '0.72rem' }}>
                        {log.alarmStatus || '--'}
                      </Badge>
                    </td>
                    <td style={{ fontSize: '0.78rem', padding: '8px 12px' }}>{log.humidity ?? '--'}</td>
                    <td style={{ fontSize: '0.78rem', padding: '8px 12px' }}>{log.temperature ?? '--'}</td>
                    <td style={{ fontSize: '0.78rem', padding: '8px 12px' }}>{log.ph ?? '--'}</td>
                    <td style={{ fontSize: '0.78rem', padding: '8px 12px' }}>{log.grain || '--'}</td>
                    <td style={{ fontSize: '0.78rem', padding: '8px 12px' }}>{log.minHumidity || '--'}</td>
                    <td style={{ fontSize: '0.78rem', padding: '8px 12px' }}>{log.maxHumidity || '--'}</td>
                    <td style={{ fontSize: '0.78rem', padding: '8px 12px' }}>{log.lastEditedByEmail || '--'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer style={{ borderTop: `1px solid ${verde}20`, justifyContent: 'space-between' }}>
          <small className="text-muted">
            {logsModal.logs.length > 0 && `${logsModal.logs.length} registro(s) — ordenado por data desc`}
          </small>
          <div className="d-flex gap-2">
            <Button
              variant="outline-success"
              size="sm"
              className="rounded-pill px-3 d-flex align-items-center gap-1"
              disabled={logsModal.logs.length === 0 || logsModal.loading}
              onClick={handleDownloadCsv}
            >
              <HiOutlineDownload size={15} />
              Baixar CSV
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              className="rounded-pill px-4"
              onClick={() => setLogsModal(prev => ({ ...prev, show: false }))}
            >
              Fechar
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AlarmConfigForm;
